import React, {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useState,
} from 'react';

import { AccountInfo, AuthenticationResult } from '@azure/msal-browser';

import {
  removeMatrixToken,
  setMatrixToken,
  setMatrixUserId,
  setMatrixStoreId,
  setMatrixEmployeeId,
  setRefreshToken,
  getRefreshToken,
  getMatrixToken,
  removeAllCookies,
  setMatrixPaymentAccessToken,
  setPaymentRefreshToken,
} from 'utils/cookie';

import axios from 'axios';
import Login from 'components/login';
import MultiStore from 'components/login/MultiStore';

import { notification, Spin } from 'antd';

import { encryptData } from 'utils/encrypt';
import {
  ALERT,
  COMMON_BUTTON_LABELS,
  ROUTE_CONFIG,
  USER_DETAILS_ERROR,
} from 'globalConstants';
import { getNotification } from 'utils/util';
import { clearCacheOnSignOut } from 'utils/cacheServices';
import FeatureFlagProvider from './FeatureFlagProvider';
import {
  DISABLE_KEYS,
  DISABLE_KEYS_WITH_CTRL,
  DISABLE_KEYS_WITH_CTRL_SHIFT,
  DISABLED_KEY_EVENT_MSG,
} from './constants';
import { getStoreEndDayStatus } from 'services/cashManagement';
import ControlledAlert from 'components/common/ControlledAlert';
import { END_DAY_WARNING_STORE_POPUP } from 'components/layout/constants';

export const AuthContext = createContext<Auth>({
  isAuthenticated: false,
  authenticationError: false,
});

export const useAuth = () => useContext<Auth>(AuthContext);

export interface Auth {
  isAuthenticated: boolean;
  authenticationError: boolean;
  authenticationErrorMessage?: string;
  userFullName?: string;
  userProfilePhoto?: string;
  signOut?: () => void;
  getMatrixAccessToken?: () => Promise<AuthenticationResult | undefined>;
}

interface AuthProviderProps {
  children: ReactNode;
}

const AuthProvider = ({ children }: AuthProviderProps) => {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [userName, setUserName] = useState<string | undefined>('');
  const [currentUserResponse, setCurrentUserResponse] = useState<any>({});
  const [isMultipleStore, setIsMultipleStore] = useState<boolean>(false);
  const [isEndDayStoreStatusPopupVisible, setEndDayStoreStatusPopupVisible] =
    useState<boolean>(false);

  useEffect(() => {
    //not showing refresh token error on APP LOAD
    if (getRefreshToken() && getMatrixToken()) {
      setIsAuthenticated(true);
      refreshTokenHandler();
    }
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    // to prevent right click on context menu
    const handleContextMenu = (event: any) => {
      event.preventDefault();
    };

    // Add the event listener to the document
    const isDisableKeyboardEvents = JSON.parse(
      process.env.REACT_APP_DISABLE_KEYBOARD_EVENTS || 'true'
    );

    if (isDisableKeyboardEvents) {
      document.addEventListener('contextmenu', handleContextMenu);
      document.addEventListener('keydown', disableKeyBoardEvents);
    }

    // Clean up the event listener on component unmount
    return () => {
      if (isDisableKeyboardEvents) {
        document.removeEventListener('contextmenu', handleContextMenu);
        document.removeEventListener('keydown', disableKeyBoardEvents);
      }
    };
  }, []);

  const disableKeyBoardEvents = (event: any) => {
    const { key, ctrlKey, shiftKey } = event;

    if (ctrlKey && shiftKey) {
      if (!DISABLE_KEYS_WITH_CTRL_SHIFT.includes(key)) return;
    } else if (ctrlKey) {
      if (!DISABLE_KEYS_WITH_CTRL.includes(key)) return;
    } else if (!DISABLE_KEYS.includes(key)) return;

    event.preventDefault();

    notification.error({
      message: DISABLED_KEY_EVENT_MSG,
      duration: 5,
    });
  };

  const getUserAndStore = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_DOMAIN}/api/users/currentuser`,
        {
          headers: {
            Authorization: `Bearer ${getMatrixToken()}`,
          },
        }
      );

      if (response?.data) {
        if (response?.data?.stores?.length > 0) {
          if (!response?.data?.userId || !response?.data?.stores[0]) {
            getNotification(ALERT.STATUS.ERROR, USER_DETAILS_ERROR);
          }

          setCurrentUserResponse(response?.data);
          setMatrixUserId(response?.data?.userId);
          setMatrixEmployeeId(response?.data?.employeeId);
          setUserName(response?.data?.displayName);

          if (response?.data?.stores?.length > 1) setIsMultipleStore(true);
          else setMatrixStoreId(response?.data?.stores[0]);

          setIsLoading(false);
        } else {
          setIsLoading(false);
          signOut();
        }
      }
    } catch (e: any) {
      // For any non-successful status code, do not take any action
      setMatrixUserId('');
      setMatrixStoreId('');
      setMatrixEmployeeId('');
      setIsLoading(false);
    }
  };

  const signOut = () => {
    if (window?.location?.pathname === `/${ROUTE_CONFIG.CONSUMER_DUTY}`) {
      window.location.replace('/');
    }

    clearCacheOnSignOut();
    setIsAuthenticated(false);
    removeAllCookies();
  };

  //need to be removed
  const getMatrixAccessToken = async (
    accountParam?: AccountInfo
  ): Promise<AuthenticationResult | undefined> => {
    return;
  };

  //HandT User login methods from here
  const loginHandler = async (payload: any) => {
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_DOMAIN}/api/users/login`,
        payload
      );

      if (response.status === 200) authenticationHandler(response.data);
    } catch (error: any) {
      getNotification(ALERT.STATUS.ERROR, error?.response?.data?.error);
      setIsLoading(false);
    }
  };

  const refreshTokenHandler = async () => {
    const payload = {
      refreshToken: getRefreshToken(),
    };
    try {
      setIsLoading(true);
      const response = await axios.post(
        `${process.env.REACT_APP_API_DOMAIN}/api/users/refresh-token`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${getMatrixToken()}`,
          },
        }
      );

      if (response.status === 200) authenticationHandler(response.data);
    } catch (error: any) {
      setIsLoading(false);
      setIsAuthenticated(false);
    }
  };

  const authenticationHandler = async (params: any) => {
    const accessToken = params.access_token;
    const refreshToken = params.refresh_token;
    const paymentRefreshToken = params.payment_refresh_token;
    const paymentAccessToken = params.payment_access_token;

    setAccessToken(accessToken, paymentAccessToken);
    setRefreshToken(refreshToken);
    setPaymentRefreshToken(paymentRefreshToken);
    setIsAuthenticated(true);
    await getUserAndStore();
  };

  const setAccessToken = (
    accessToken: string,
    paymentAccessToken: string = ''
  ) => {
    removeMatrixToken();
    setMatrixToken(accessToken);
    setMatrixPaymentAccessToken(paymentAccessToken);
  };

  const onLoginClick = (params: any) => {
    const payload = {
      userName: params.userName,
      password: encryptData(params.password),
    };

    setIsLoading(true);
    loginHandler(payload);
  };

  const getStoreEndOfDayStatus = async () => {
    try {
      const response = await getStoreEndDayStatus();
      if (response?.data?.isCompleted) setEndDayStoreStatusPopupVisible(true);
    } catch (e: any) {
      // Do nothing
    }
  };

  const storeHandler = (storeDetails: any) => {
    setMatrixStoreId(storeDetails);
    setIsMultipleStore(false);
    getStoreEndOfDayStatus();
  };

  if (!isAuthenticated)
    return <Login loginHandler={onLoginClick} isLoading={isLoading} />;

  if (isAuthenticated && isLoading)
    return (
      <div style={{ margin: '50px 0', textAlign: 'center' }}>
        <Spin />
      </div>
    );

  if (isMultipleStore)
    return (
      <MultiStore
        userName={userName}
        stores={currentUserResponse?.stores}
        storeHandler={storeHandler}
      />
    );

  if (isEndDayStoreStatusPopupVisible)
    return (
      <ControlledAlert
        visible={isEndDayStoreStatusPopupVisible}
        width={600}
        itemProp={'secondary'}
        alertMessage={END_DAY_WARNING_STORE_POPUP.WARNING}
        message={END_DAY_WARNING_STORE_POPUP.MESSAGE}
        secondaryMessage={END_DAY_WARNING_STORE_POPUP.SECONDARY_MSG}
        yesButtonText={COMMON_BUTTON_LABELS.OK}
        isCancelBtn={false}
        onClick={() => setEndDayStoreStatusPopupVisible(false)}
      />
    );

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated: isAuthenticated,
        authenticationError: false,
        authenticationErrorMessage: '',
        userFullName: userName,
        userProfilePhoto: '',
        signOut: signOut,
        getMatrixAccessToken: getMatrixAccessToken,
      }}
    >
      <FeatureFlagProvider>{children}</FeatureFlagProvider>
    </AuthContext.Provider>
  );
};

export default AuthProvider;
